import React from 'react';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import { Typography, Paper } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
import classNames from 'classnames';
import { ReactComponent as SearchIcon } from 'Images/icons/ic_list_search_active.svg';

const styles = {
  root: {
    height: 60,
    display: 'flex',
    justifyContent: 'space-between',
  },
  iconButton: {
    transform: 'scale(1, 1)',
    transition: 'transform 200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
  },
  iconButtonHidden: {
    transform: 'scale(0, 0)',
    '& > $icon': {
      opacity: 0,
    },
  },
  searchIconButton: {
    marginRight: -48,
  },
  icon: {
    transition: 'opacity 200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
  },
  input: {
    width: '100%',
  },
  searchContainer: {
    margin: 'auto 16px',
    width: 'calc(100% - 125px - 40px)', // 48px button + 32px margin
  },
  clear: {
    color: '#f5a623',
    fontSize: '14px',
  },
};

/**
 * Material design search bar
 * @see [Search patterns](https://material.io/guidelines/patterns/search.html)
 */
class SearchBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      value: this.props.value,
      error: '',
    };
    this.handleInput = this.handleInput.bind(this);
  }

  handleInput = e => {
    this.setState({ value: e.target.value });
    if (e.target.value.length > 3 || e.target.value === '') {
      this.props.onRequestSearch(e.target.value);
      this.setState({ error: '' });
    } else {
      this.setState({ error: 'Please enter 4 or more characters' });
    }
  };

  handleCancel = () => {
    this.setState({ value: '' });
    this.props.onRequestSearch('');
  };

  handleKeyUp = e => {
    if (e.charCode === 13 || e.key === 'Enter') {
      this.handleRequestSearch();
    } else if (
      this.props.cancelOnEscape &&
      (e.charCode === 27 || e.key === 'Escape')
    ) {
      this.handleCancel();
    }
  };

  handleRequestSearch = () => {
    const { value } = this.state;
    if (value.length > 3 || value === '') {
      this.props.onRequestSearch(value);
    }
  };

  render() {
    const { value, error } = this.state;
    const { classes } = this.props;

    return (
      <div>
        <Paper className={classes.root}>
          <IconButton
            onClick={this.handleRequestSearch}
            classes={{
              root: classNames(classes.iconButton, classes.searchIconButton),
            }}
          >
            {React.cloneElement(<SearchIcon />, {
              classes: { root: classes.icon },
            })}
          </IconButton>
          <div className={classes.searchContainer}>
            <Input
              placeholder="Search"
              value={value}
              onChange={this.handleInput}
              onKeyUp={this.handleKeyUp}
              fullWidth
              className={classes.input}
              disableUnderline
            />
          </div>
          <IconButton
            onClick={this.handleCancel}
            classes={{
              root: classNames(classes.iconButton, {
                [classes.iconButtonHidden]: value === '',
              }),
            }}
          >
            {React.cloneElement(
              <Typography className={classes.clear}>CLEAR</Typography>
            )}
          </IconButton>
        </Paper>
        {error && <span className="error">{error}</span>}
      </div>
    );
  }
}

SearchBar.defaultProps = {
  value: '',
};

SearchBar.propTypes = {
  /** Whether to clear search on escape */
  cancelOnEscape: PropTypes.bool,
  /** Fired when the search icon is clicked. */
  onRequestSearch: PropTypes.func.isRequired,
  /** The value of the text field. */
  value: PropTypes.string,
  classes: PropTypes.object,
};

export default withStyles(styles)(SearchBar);
